import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { 
  Calendar as CalendarIcon,
  Clock,
  Video,
  MapPin,
  Phone,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Send,
  RefreshCw,
  ArrowRight,
  CheckCheck,
  Star,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Edit,
  Save
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Meeting type definition
interface Meeting {
  id: string;
  pitchId: string;
  analystId: string;
  founderId: string;
  proposedDates: {
    date: Date;
    time: string;
  }[];
  selectedDateIndex?: number;
  confirmedDate?: Date;
  confirmedTime?: string;
  duration: number;
  agenda: string[];
  meetingType: 'video' | 'phone' | 'in-person';
  meetingLink?: string;
  location?: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'rescheduled';
  notes?: string;
  meetingNotes?: {
    summary: string;
    keyPoints: string[];
    actionItems: string[];
    nextSteps: string;
    founderRating?: {
      understanding: number;
      commitment: number;
      responsiveness: number;
    };
    pitchRating?: {
      problemClarity: number;
      solutionFeasibility: number;
      marketPotential: number;
      teamCapability: number;
    };
    redFlags: string[];
    recommendation: 'proceed' | 'revision' | 'reject';
    requiresSeniorReview: boolean;
    recordedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface MeetingPanelProps {
  pitch: any;
}

export default function MeetingPanel({ pitch }: MeetingPanelProps) {
  const { user } = useAuth();
  const { updatePitchStage } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const action = searchParams.get('action');

  // Load meetings from localStorage
  const [meetings, setMeetings] = useState<Meeting[]>(() => {
    const saved = localStorage.getItem(`meetings_${pitch.id}`);
    return saved ? JSON.parse(saved) : [];
  });

  // Dialog states
  const [showScheduleDialog, setShowScheduleDialog] = useState(action === 'schedule');
  const [showNotesDialog, setShowNotesDialog] = useState(action === 'notes');
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  // Form states - Scheduling
  const [proposedDates, setProposedDates] = useState([{ date: new Date(), time: '10:00' }]);
  const [meetingDuration, setMeetingDuration] = useState('60');
  const [agenda, setAgenda] = useState(['Review pitch deck', 'Discuss solution', 'Next steps']);
  const [meetingType, setMeetingType] = useState<'video' | 'phone' | 'in-person'>('video');
  const [meetingLink, setMeetingLink] = useState('');
  const [location, setLocation] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Form states - Meeting Notes
  const [meetingSummary, setMeetingSummary] = useState('');
  const [keyPoints, setKeyPoints] = useState(['']);
  const [actionItems, setActionItems] = useState(['']);
  const [nextSteps, setNextSteps] = useState('');
  
  // Ratings
  const [founderUnderstanding, setFounderUnderstanding] = useState(3);
  const [founderCommitment, setFounderCommitment] = useState(3);
  const [founderResponsiveness, setFounderResponsiveness] = useState(3);
  const [problemClarity, setProblemClarity] = useState(3);
  const [solutionFeasibility, setSolutionFeasibility] = useState(3);
  const [marketPotential, setMarketPotential] = useState(3);
  const [teamCapability, setTeamCapability] = useState(3);
  const [redFlags, setRedFlags] = useState(['']);
  const [recommendation, setRecommendation] = useState<'proceed' | 'revision' | 'reject'>('proceed');
  const [requiresSeniorReview, setRequiresSeniorReview] = useState(false);

  // Save meetings to localStorage
  const saveMeetings = (updatedMeetings: Meeting[]) => {
    localStorage.setItem(`meetings_${pitch.id}`, JSON.stringify(updatedMeetings));
    setMeetings(updatedMeetings);
  };

  // Check if user is founder or analyst
  const isFounder = user?.id === pitch.founderId;
  const isAnalyst = user?.id === pitch.assignedAnalystId;
  const isSeniorAnalyst = user?.role === 'senior_analyst';

  // ========== MEETING SCHEDULING ==========

  const addProposedDate = () => {
    setProposedDates([...proposedDates, { date: new Date(), time: '10:00' }]);
  };

  const updateProposedDate = (index: number, field: 'date' | 'time', value: Date | string) => {
    const newDates = [...proposedDates];
    if (field === 'date') {
      newDates[index].date = value as Date;
    } else {
      newDates[index].time = value as string;
    }
    setProposedDates(newDates);
  };

  const removeProposedDate = (index: number) => {
    setProposedDates(proposedDates.filter((_, i) => i !== index));
  };

  const addAgendaItem = () => {
    setAgenda([...agenda, '']);
  };

  const updateAgendaItem = (index: number, value: string) => {
    const newAgenda = [...agenda];
    newAgenda[index] = value;
    setAgenda(newAgenda);
  };

  const removeAgendaItem = (index: number) => {
    setAgenda(agenda.filter((_, i) => i !== index));
  };

  const handleScheduleMeeting = () => {
    if (!user) return;

    const newMeeting: Meeting = {
      id: `meeting-${Date.now()}`,
      pitchId: pitch.id,
      analystId: pitch.assignedAnalystId || '',
      founderId: pitch.founderId,
      proposedDates: proposedDates.map(pd => ({
        date: pd.date,
        time: pd.time
      })),
      duration: parseInt(meetingDuration),
      agenda: agenda.filter(a => a.trim() !== ''),
      meetingType,
      meetingLink: meetingType === 'video' ? meetingLink : undefined,
      location: meetingType === 'in-person' ? location : undefined,
      notes: additionalNotes,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedMeetings = [...meetings, newMeeting];
    saveMeetings(updatedMeetings);
    setShowScheduleDialog(false);
    setSearchParams({ tab: 'meeting' });

    toast({
      title: "Meeting proposal sent",
      description: "The founder has been notified to confirm a time.",
    });
  };

  // ========== MEETING CONFIRMATION ==========

  const handleConfirmMeeting = (meeting: Meeting, dateIndex: number) => {
    const updatedMeetings = meetings.map(m => {
      if (m.id === meeting.id) {
        return {
          ...m,
          status: 'confirmed',
          selectedDateIndex: dateIndex,
          confirmedDate: m.proposedDates[dateIndex].date,
          confirmedTime: m.proposedDates[dateIndex].time,
          updatedAt: new Date().toISOString()
        };
      }
      return m;
    });

    saveMeetings(updatedMeetings);

    toast({
      title: "Meeting confirmed",
      description: `Meeting scheduled for ${format(meeting.proposedDates[dateIndex].date, 'PPP')} at ${meeting.proposedDates[dateIndex].time}`,
    });
  };

  // ========== MEETING RESCHEDULING ==========

  const handleRescheduleClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setProposedDates(meeting.proposedDates.map(pd => ({ date: pd.date, time: pd.time })));
    setMeetingDuration(meeting.duration.toString());
    setAgenda(meeting.agenda);
    setMeetingType(meeting.meetingType);
    setMeetingLink(meeting.meetingLink || '');
    setLocation(meeting.location || '');
    setAdditionalNotes(meeting.notes || '');
    setShowRescheduleDialog(true);
  };

  const handleReschedule = () => {
    if (!selectedMeeting) return;

    const updatedMeetings = meetings.map(m => {
      if (m.id === selectedMeeting.id) {
        return {
          ...m,
          proposedDates: proposedDates.map(pd => ({
            date: pd.date,
            time: pd.time
          })),
          status: 'pending',
          updatedAt: new Date().toISOString()
        };
      }
      return m;
    });

    saveMeetings(updatedMeetings);
    setShowRescheduleDialog(false);
    setSelectedMeeting(null);

    toast({
      title: "Reschedule request sent",
      description: "The other party has been notified of the new times.",
    });
  };

  // ========== MEETING COMPLETION ==========

  const handleCompleteMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setMeetingSummary('');
    setKeyPoints(['']);
    setActionItems(['']);
    setNextSteps('');
    setFounderUnderstanding(3);
    setFounderCommitment(3);
    setFounderResponsiveness(3);
    setProblemClarity(3);
    setSolutionFeasibility(3);
    setMarketPotential(3);
    setTeamCapability(3);
    setRedFlags(['']);
    setRecommendation('proceed');
    setRequiresSeniorReview(false);
    setShowNotesDialog(true);
  };

  // ========== MEETING NOTES ==========

  const addKeyPoint = () => {
    setKeyPoints([...keyPoints, '']);
  };

  const updateKeyPoint = (index: number, value: string) => {
    const newPoints = [...keyPoints];
    newPoints[index] = value;
    setKeyPoints(newPoints);
  };

  const removeKeyPoint = (index: number) => {
    setKeyPoints(keyPoints.filter((_, i) => i !== index));
  };

  const addActionItem = () => {
    setActionItems([...actionItems, '']);
  };

  const updateActionItem = (index: number, value: string) => {
    const newItems = [...actionItems];
    newItems[index] = value;
    setActionItems(newItems);
  };

  const removeActionItem = (index: number) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  const addRedFlag = () => {
    setRedFlags([...redFlags, '']);
  };

  const updateRedFlag = (index: number, value: string) => {
    const newFlags = [...redFlags];
    newFlags[index] = value;
    setRedFlags(newFlags);
  };

  const removeRedFlag = (index: number) => {
    setRedFlags(redFlags.filter((_, i) => i !== index));
  };

  const handleSaveNotes = () => {
    if (!selectedMeeting) return;

    const updatedMeetings = meetings.map(m => {
      if (m.id === selectedMeeting.id) {
        return {
          ...m,
          status: 'completed',
          meetingNotes: {
            summary: meetingSummary,
            keyPoints: keyPoints.filter(kp => kp.trim() !== ''),
            actionItems: actionItems.filter(ai => ai.trim() !== ''),
            nextSteps: nextSteps,
            founderRating: {
              understanding: founderUnderstanding,
              commitment: founderCommitment,
              responsiveness: founderResponsiveness
            },
            pitchRating: {
              problemClarity,
              solutionFeasibility,
              marketPotential,
              teamCapability
            },
            redFlags: redFlags.filter(rf => rf.trim() !== ''),
            recommendation,
            requiresSeniorReview,
            recordedAt: new Date().toISOString()
          },
          updatedAt: new Date().toISOString()
        };
      }
      return m;
    });

    saveMeetings(updatedMeetings);
    setShowNotesDialog(false);
    setSelectedMeeting(null);

    // If recommendation is to proceed, move to contract stage
    if (recommendation === 'proceed' && isAnalyst) {
      updatePitchStage(pitch.id, 'contract');
      toast({
        title: "Moving to Contract Stage",
        description: "Meeting completed successfully. Proceeding to contract negotiation.",
      });
    } else {
      toast({
        title: "Meeting notes saved",
        description: "Notes have been recorded successfully.",
      });
    }
  };

  // ========== MEETING CANCELLATION ==========

  const handleCancelMeeting = (meetingId: string) => {
    const updatedMeetings = meetings.map(m => {
      if (m.id === meetingId) {
        return {
          ...m,
          status: 'cancelled',
          updatedAt: new Date().toISOString()
        };
      }
      return m;
    });

    saveMeetings(updatedMeetings);

    toast({
      title: "Meeting cancelled",
      description: "The meeting has been cancelled.",
    });
  };

  // ========== RENDER ==========

  const pendingMeetings = meetings.filter(m => m.status === 'pending');
  const confirmedMeetings = meetings.filter(m => m.status === 'confirmed');
  const completedMeetings = meetings.filter(m => m.status === 'completed');
  const needsNotes = meetings.filter(m => m.status === 'completed' && !m.meetingNotes);

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">Meeting Management</h2>
          <p className="text-muted-foreground">Schedule and manage meetings with the founder</p>
        </div>
        {isAnalyst && (
          <Button onClick={() => setShowScheduleDialog(true)}>
            <CalendarIcon className="h-4 w-4 mr-2" />
            Schedule New Meeting
          </Button>
        )}
      </div>

      {/* Meeting stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{pendingMeetings.length}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{confirmedMeetings.length}</p>
              <p className="text-xs text-muted-foreground">Confirmed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{completedMeetings.length}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{needsNotes.length}</p>
              <p className="text-xs text-muted-foreground">Need Notes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Meetings */}
      {pendingMeetings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Pending Meetings
            </CardTitle>
            <CardDescription>Awaiting confirmation from {isFounder ? 'analyst' : 'founder'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingMeetings.map(meeting => (
              <div key={meeting.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="font-medium">Meeting Proposal</p>
                    <p className="text-sm text-muted-foreground">
                      Proposed by {meeting.analystId === user?.id ? 'you' : 'analyst'}
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-yellow-50">Pending</Badge>
                </div>

                {/* Proposed dates */}
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-medium">Proposed Dates:</p>
                  {meeting.proposedDates.map((pd, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <span className="text-sm">
                        Option {idx + 1}: {format(new Date(pd.date), 'PPP')} at {pd.time}
                      </span>
                      {isFounder && meeting.status === 'pending' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600"
                          onClick={() => handleConfirmMeeting(meeting, idx)}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Select
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Meeting details */}
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-muted-foreground">Duration:</span>{' '}
                    <span className="font-medium">{meeting.duration} min</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    <span className="font-medium capitalize">{meeting.meetingType}</span>
                  </div>
                </div>

                {/* Agenda */}
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Agenda:</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {meeting.agenda.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleRescheduleClick(meeting)}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Reschedule
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleCancelMeeting(meeting.id)}
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Confirmed Meetings */}
      {confirmedMeetings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Confirmed Meetings
            </CardTitle>
            <CardDescription>Scheduled and confirmed meetings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {confirmedMeetings.map(meeting => {
              const selectedDate = meeting.selectedDateIndex !== undefined 
                ? meeting.proposedDates[meeting.selectedDateIndex] 
                : null;

              return (
                <div key={meeting.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-medium">Confirmed Meeting</p>
                      {selectedDate && (
                        <p className="text-sm">
                          {format(new Date(selectedDate.date), 'PPP')} at {selectedDate.time}
                        </p>
                      )}
                    </div>
                    <Badge className="bg-green-500">Confirmed</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-muted-foreground">Duration:</span>{' '}
                      <span className="font-medium">{meeting.duration} min</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>{' '}
                      <span className="font-medium capitalize">{meeting.meetingType}</span>
                    </div>
                  </div>

                  {meeting.meetingLink && (
                    <div className="mb-4">
                      <a 
                        href={meeting.meetingLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                      >
                        <Video className="h-3 w-3" />
                        Join Meeting
                      </a>
                    </div>
                  )}

                  {meeting.location && (
                    <div className="mb-4 text-sm flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {meeting.location}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {isAnalyst && (
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleCompleteMeeting(meeting)}
                      >
                        <CheckCheck className="h-3 w-3 mr-1" />
                        Mark Complete & Add Notes
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRescheduleClick(meeting)}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Reschedule
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Completed Meetings */}
      {completedMeetings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCheck className="h-5 w-5 text-blue-500" />
              Completed Meetings
            </CardTitle>
            <CardDescription>Past meetings with notes</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {completedMeetings.map(meeting => (
              <div key={meeting.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">
                      {meeting.confirmedDate && format(new Date(meeting.confirmedDate), 'PPP')}
                    </p>
                  </div>
                  {!meeting.meetingNotes ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700">
                      Notes Missing
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      Notes Added
                    </Badge>
                  )}
                </div>

                {meeting.meetingNotes ? (
                  <div className="space-y-3 mt-3">
                    <div>
                      <p className="text-sm font-medium">Summary</p>
                      <p className="text-sm text-muted-foreground">{meeting.meetingNotes.summary}</p>
                    </div>

                    {meeting.meetingNotes.recommendation && (
                      <div>
                        <p className="text-sm font-medium">Recommendation</p>
                        <Badge className={
                          meeting.meetingNotes.recommendation === 'proceed' ? 'bg-green-500' :
                          meeting.meetingNotes.recommendation === 'revision' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }>
                          {meeting.meetingNotes.recommendation}
                        </Badge>
                      </div>
                    )}

                    {meeting.meetingNotes.requiresSeniorReview && (
                      <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-700">
                          This meeting requires senior analyst review
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedMeeting(meeting);
                        setShowNotesDialog(true);
                        setMeetingSummary(meeting.meetingNotes?.summary || '');
                        setKeyPoints(meeting.meetingNotes?.keyPoints || ['']);
                        setActionItems(meeting.meetingNotes?.actionItems || ['']);
                        setNextSteps(meeting.meetingNotes?.nextSteps || '');
                      }}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      View/Edit Notes
                    </Button>
                  </div>
                ) : (
                  isAnalyst && (
                    <Button 
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => handleCompleteMeeting(meeting)}
                    >
                      <MessageSquare className="h-3 w-3 mr-1" />
                      Add Meeting Notes
                    </Button>
                  )
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No meetings state */}
      {meetings.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">No meetings scheduled</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isAnalyst 
                ? "Schedule a meeting with the founder to discuss the pitch."
                : "The analyst will schedule a meeting to discuss your pitch."}
            </p>
            {isAnalyst && (
              <Button onClick={() => setShowScheduleDialog(true)} className="mt-4">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Schedule First Meeting
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ========== SCHEDULE MEETING DIALOG ========== */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Meeting with Founder</DialogTitle>
            <DialogDescription>
              Propose multiple date/time options for the founder to choose from
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Proposed Dates */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label>Proposed Date & Time Options</Label>
                <Button type="button" variant="outline" size="sm" onClick={addProposedDate}>
                  Add Option
                </Button>
              </div>
              
              {proposedDates.map((dateOption, index) => (
                <div key={index} className="flex gap-4 items-start p-4 border rounded-lg">
                  <div className="flex-1 space-y-4">
                    <div className="space-y-2">
                      <Label>Date {index + 1}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !dateOption.date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateOption.date ? format(dateOption.date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dateOption.date}
                            onSelect={(date) => date && updateProposedDate(index, 'date', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Select 
                        value={dateOption.time} 
                        onValueChange={(value) => updateProposedDate(index, 'time', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="09:00">09:00 AM</SelectItem>
                          <SelectItem value="10:00">10:00 AM</SelectItem>
                          <SelectItem value="11:00">11:00 AM</SelectItem>
                          <SelectItem value="12:00">12:00 PM</SelectItem>
                          <SelectItem value="13:00">01:00 PM</SelectItem>
                          <SelectItem value="14:00">02:00 PM</SelectItem>
                          <SelectItem value="15:00">03:00 PM</SelectItem>
                          <SelectItem value="16:00">04:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {proposedDates.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProposedDate(index)}
                      className="shrink-0"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label>Meeting Duration</Label>
              <Select value={meetingDuration} onValueChange={setMeetingDuration}>
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="90">90 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Type */}
            <div className="space-y-2">
              <Label>Meeting Type</Label>
              <Select value={meetingType} onValueChange={(value: 'video'|'phone'|'in-person') => setMeetingType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select meeting type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="video">Video Call</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                  <SelectItem value="in-person">In Person</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Link/Location */}
            {meetingType === 'video' && (
              <div className="space-y-2">
                <Label>Video Meeting Link</Label>
                <Input
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            )}

            {meetingType === 'in-person' && (
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Office address, cafe, etc."
                />
              </div>
            )}

            {/* Agenda */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Agenda Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAgendaItem}>
                  Add Item
                </Button>
              </div>
              {agenda.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateAgendaItem(index, e.target.value)}
                    placeholder={`Agenda item ${index + 1}`}
                  />
                  {agenda.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAgendaItem(index)}
                      className="shrink-0"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Any additional information for the founder..."
                className="min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleScheduleMeeting}>
              <Send className="h-4 w-4 mr-2" />
              Send Proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== MEETING NOTES DIALOG ========== */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Meeting Notes & Evaluation</DialogTitle>
            <DialogDescription>
              Document the meeting outcomes and evaluate the pitch
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Summary */}
            <div className="space-y-2">
              <Label>Meeting Summary</Label>
              <Textarea
                value={meetingSummary}
                onChange={(e) => setMeetingSummary(e.target.value)}
                placeholder="Summarize the key points discussed..."
                className="min-h-[100px]"
              />
            </div>

            {/* Key Points */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Key Discussion Points</Label>
                <Button type="button" variant="outline" size="sm" onClick={addKeyPoint}>
                  Add Point
                </Button>
              </div>
              {keyPoints.map((point, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={point}
                    onChange={(e) => updateKeyPoint(index, e.target.value)}
                    placeholder={`Key point ${index + 1}`}
                  />
                  {keyPoints.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeKeyPoint(index)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Founder Ratings */}
            <div className="space-y-4">
              <h3 className="font-medium">Founder Assessment</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Understanding (1-5)</Label>
                  <Select value={founderUnderstanding.toString()} onValueChange={(v) => setFounderUnderstanding(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Commitment (1-5)</Label>
                  <Select value={founderCommitment.toString()} onValueChange={(v) => setFounderCommitment(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Responsiveness (1-5)</Label>
                  <Select value={founderResponsiveness.toString()} onValueChange={(v) => setFounderResponsiveness(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Pitch Ratings */}
            <div className="space-y-4">
              <h3 className="font-medium">Pitch Assessment</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Problem Clarity (1-5)</Label>
                  <Select value={problemClarity.toString()} onValueChange={(v) => setProblemClarity(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Solution Feasibility (1-5)</Label>
                  <Select value={solutionFeasibility.toString()} onValueChange={(v) => setSolutionFeasibility(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Market Potential (1-5)</Label>
                  <Select value={marketPotential.toString()} onValueChange={(v) => setMarketPotential(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Team Capability (1-5)</Label>
                  <Select value={teamCapability.toString()} onValueChange={(v) => setTeamCapability(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Red Flags */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-red-500">Red Flags</Label>
                <Button type="button" variant="outline" size="sm" onClick={addRedFlag}>
                  Add Red Flag
                </Button>
              </div>
              {redFlags.map((flag, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={flag}
                    onChange={(e) => updateRedFlag(index, e.target.value)}
                    placeholder="Critical issue that needs attention"
                    className="border-red-200"
                  />
                  {redFlags.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRedFlag(index)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Action Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Action Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addActionItem}>
                  Add Action
                </Button>
              </div>
              {actionItems.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => updateActionItem(index, e.target.value)}
                    placeholder="Action item"
                  />
                  {actionItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeActionItem(index)}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Next Steps */}
            <div className="space-y-2">
              <Label>Next Steps</Label>
              <Textarea
                value={nextSteps}
                onChange={(e) => setNextSteps(e.target.value)}
                placeholder="What happens next?"
                className="min-h-[80px]"
              />
            </div>

            {/* Recommendation */}
            <div className="space-y-4">
              <div>
                <Label>Recommendation</Label>
                <Select value={recommendation} onValueChange={(v: any) => setRecommendation(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proceed" className="text-green-600">Proceed to Contract</SelectItem>
                    <SelectItem value="revision" className="text-yellow-600">Needs Revision</SelectItem>
                    <SelectItem value="reject" className="text-red-600">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="seniorReview"
                  checked={requiresSeniorReview}
                  onChange={(e) => setRequiresSeniorReview(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="seniorReview">
                  Requires senior analyst review before proceeding
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes}>
              <Save className="h-4 w-4 mr-2" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== RESCHEDULE DIALOG ========== */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reschedule Meeting</DialogTitle>
            <DialogDescription>
              Propose new dates for the meeting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Same date picker UI as schedule dialog */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label>New Proposed Dates</Label>
                <Button type="button" variant="outline" size="sm" onClick={addProposedDate}>
                  Add Option
                </Button>
              </div>
              
              {proposedDates.map((dateOption, index) => (
                <div key={index} className="flex gap-4 items-start p-4 border rounded-lg">
                  <div className="flex-1 space-y-4">
                    <div className="space-y-2">
                      <Label>Date {index + 1}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !dateOption.date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateOption.date ? format(dateOption.date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dateOption.date}
                            onSelect={(date) => date && updateProposedDate(index, 'date', date)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Select 
                        value={dateOption.time} 
                        onValueChange={(value) => updateProposedDate(index, 'time', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="09:00">09:00 AM</SelectItem>
                          <SelectItem value="10:00">10:00 AM</SelectItem>
                          <SelectItem value="11:00">11:00 AM</SelectItem>
                          <SelectItem value="12:00">12:00 PM</SelectItem>
                          <SelectItem value="13:00">01:00 PM</SelectItem>
                          <SelectItem value="14:00">02:00 PM</SelectItem>
                          <SelectItem value="15:00">03:00 PM</SelectItem>
                          <SelectItem value="16:00">04:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {proposedDates.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProposedDate(index)}
                      className="shrink-0"
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Reason for rescheduling..."
                className="min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule}>
              <Send className="h-4 w-4 mr-2" />
              Send Reschedule Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}